import React from "react";
import { useRouter } from "next/router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "@/redux/hooks";
import { logOutUser } from "@/services/auth";
import { isInternalSupportAccessEnabled } from "@/services/support-access";
import {
  clearSupportMode,
  getSupportTenantName,
  isSupportMode,
} from "@/utils/support-access";

export default function SupportAccessBanner() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [visible, setVisible] = React.useState(false);
  const [tenantName, setTenantName] = React.useState("");

  React.useEffect(() => {
    const syncSupportMode = async () => {
      if (!isSupportMode()) {
        setVisible(false);
        return;
      }

      const supportAccessEnabled = await isInternalSupportAccessEnabled();

      if (!supportAccessEnabled) {
        clearSupportMode();
        dispatch({ type: "RESET" });
        await logOutUser();
        await router.push("/sign-in");
        return;
      }

      setVisible(true);
      setTenantName(getSupportTenantName());
    };

    void syncSupportMode();
  }, [dispatch, router, router.asPath]);

  if (!visible) {
    return null;
  }

  const handleExit = async () => {
    clearSupportMode();
    dispatch({ type: "RESET" });
    await logOutUser();
    await router.push("/sign-in");
  };

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        width: "100%",
        boxSizing: "border-box",
        background: "#651F70",
        color: "#fff",
        minHeight: 48,
        px: { xs: 2, sm: 3 },
        py: 1,
        pb: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: 2,
        flexWrap: { xs: "wrap", md: "nowrap" },
        boxShadow: "0 -4px 12px rgba(0,0,0,0.16)",
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, flex: "1 1 auto" }}>
        Support mode active{tenantName ? `: ${tenantName}` : ""}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => router.push("/support/tenants")}
          sx={{
            color: "#fff",
            borderColor: "rgba(255,255,255,0.7)",
            textTransform: "none",
            width: "auto",
            minWidth: 128,
            px: 2,
            py: 1,
            m: 0,
            whiteSpace: "nowrap",
          }}
        >
          Switch tenant
        </Button>
        <Button
          variant="contained"
          onClick={handleExit}
          sx={{
            background: "#fff",
            color: "#651F70",
            textTransform: "none",
            width: "auto",
            minWidth: 96,
            px: 2,
            py: 1,
            m: 0,
            whiteSpace: "nowrap",
          }}
        >
          Exit
        </Button>
      </Box>
    </Box>
  );
}
